/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Standardized API utilities for React Query
 * 
 * This module provides utility functions for creating standardized React Query
 * query and mutation functions that work with the API response format defined
 * in shared/types/api.ts
 */

import { QueryFunction, QueryKey } from "@tanstack/react-query";
import { handleApiResponse } from "./apiResponse";
import { getEnv, isAuthDisabled, isBrowser, isElectronApp } from "../utils/env";
import { isRemoteAuthEnabled } from "../utils/remoteAuth";
import { getMasterAuthEndpointUrl } from "../services/masterServerConfig";

/**
 * Get the base URL for API requests
 * 
 * SkillShop Multi-App Architecture:
 * - Development: Each app runs on its own port (learning-client: 5174, product: 3001, etc.)
 *   but ALL API calls must go to the main server on port 5173
 * - Web Production: All apps are served from the same origin as the main server
 * - Electron: Multi-server architecture with SkillShop on 5173, Velocity on 3001/3002
 */
function getApiBaseUrl(): string {
  if (isBrowser()) {
    const currentPort = window.location.port;
    const currentUrl = window.location.href;
    
    // Determine if this is a production web deployment (no port or standard web ports)
    // In all other cases (development, Electron, etc.), we use port 5173
    const isWebProduction = !currentPort || currentPort === '80' || currentPort === '443';
    
    let baseUrl: string;
    if (isWebProduction) {
      // Web production: all apps are served from the main server origin
      baseUrl = window.location.origin;
    } else {
      // Development or Electron: all API calls go to the main server (port 5173)
      // regardless of which app is making the request
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const mainServerPort = 5173; // Main server port from apps.config.ts
      baseUrl = `${protocol}//${hostname}:${mainServerPort}`;
    }
    
    return baseUrl;
  }
  return getEnv('API_BASE_URL', 'http://localhost:5173')!;
}

/**
 * Makes an unauthenticated request to the SkillShop server with standardized response handling
 * Useful for authentication endpoints before tokens are available
 * 
 * This function:
 * - Routes requests to the correct SkillShop server (port 5173) in all environments
 * - Does NOT add authentication headers (use authenticatedFetch for that)
 * - Enforces API response standards by processing the response envelope
 * - Returns only the data portion of the API response
 * 
 * @param endpoint The API endpoint (must start with /) OR a full URL (http:// or https://)
 * @param options Request options
 * @returns Promise with the data from the API response (not the full Response object)
 * @throws Error if the request fails or response doesn't follow API standards
 */
export async function skillShopFetch<T = any>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: any;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {}
): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = options;
  
  // Check if endpoint is already a full URL (e.g., master server URLs in remote auth mode)
  const isFullUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
  
  let url: string;
  if (isFullUrl) {
    // Use the full URL directly (e.g., for master server auth endpoints)
    url = endpoint;
  } else {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    // Build the full URL with base URL
    const baseUrl = getApiBaseUrl();
    url = `${baseUrl}${cleanEndpoint}`;
  }
  
  // Prepare headers
  const requestHeaders: HeadersInit = { ...headers };
  
  // Add Content-Type for JSON requests
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    requestHeaders['Content-Type'] = 'application/json';
  }
  
  // Prepare request body
  let requestBody: string | FormData | undefined;
  if (body) {
    if (body instanceof FormData) {
      requestBody = body;
    } else if (typeof body === 'object') {
      requestBody = JSON.stringify(body);
    } else {
      requestBody = body;
    }
  }
  
  const response = await fetch(url, {
    method,
    headers: requestHeaders,
    body: requestBody,
    signal,
  });
  
  // Process response according to API standards
  return handleApiResponse<T>(response);
}

/**
 * Get the current authentication token
 */
function getAuthToken(): string | null {
  if (isBrowser()) {
    return localStorage.getItem('accessToken');
  }
  return null;
}

/**
 * Track refresh attempts to prevent infinite loops
 */
let refreshAttemptCount = 0;
let lastRefreshAttemptTime = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_ATTEMPT_RESET_MS = 10000; // Reset counter after 10 seconds

/**
 * Refresh the access token using the refresh token
 * 
 * In remote auth mode (Electron with master server), this sends the refresh
 * request to the master server. Otherwise, it uses the local server.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (!isBrowser()) {
    return false;
  }

  // Check if authentication is disabled in development
  if (isAuthDisabled()) {
    console.log('Authentication disabled - skipping token refresh');
    return true; // Return true to indicate "success" (no refresh needed)
  }

  // Prevent infinite refresh loops
  const now = Date.now();
  if (now - lastRefreshAttemptTime > REFRESH_ATTEMPT_RESET_MS) {
    refreshAttemptCount = 0;
  }
  lastRefreshAttemptTime = now;
  refreshAttemptCount++;
  
  if (refreshAttemptCount > MAX_REFRESH_ATTEMPTS) {
    console.error(`Token refresh failed after ${MAX_REFRESH_ATTEMPTS} attempts - preventing infinite loop`);
    return false;
  }

  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return false;
    }

    // In remote auth mode, use the master server for token refresh
    // This is critical for Electron apps that authenticate against a central auth server
    const isRemoteAuth = isRemoteAuthEnabled();
    const isElectron = isElectronApp();
    
    // Use master server if remote auth is enabled (both web and Electron modes)
    const refreshUrl = isRemoteAuth
      ? getMasterAuthEndpointUrl('REFRESH')
      : `${getApiBaseUrl()}/api/auth/refresh`;
    
    console.log(`[API] Refreshing token via ${isRemoteAuth ? 'master server' : 'local server'}: ${refreshUrl}`);

    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      console.log(`[API] Token refresh failed with status ${response.status}`);
      return false;
    }

    const result = await response.json();
    if (result.success) {
      // Store new tokens
      localStorage.setItem('accessToken', result.data.accessToken);
      localStorage.setItem('refreshToken', result.data.refreshToken);
      console.log('[API] Access token refreshed successfully');
      // Reset attempt counter on success
      refreshAttemptCount = 0;
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[API] Failed to refresh token:', error);
    return false;
  }
}

/**
 * Options for authenticated API requests
 */
export interface AuthenticatedFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: any;
  headers?: Record<string, string>;
  skipJsonHeaders?: boolean;
  signal?: AbortSignal;
}

/**
 * Makes an authenticated API request with proper base URL and auth headers
 * @param endpoint The API endpoint (must start with /) OR a full URL (http:// or https://)
 * @param options Request options
 * @returns Promise with the fetch Response
 */
export async function authenticatedFetch(
  endpoint: string, 
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const { method = 'GET', body, headers = {}, skipJsonHeaders = false, signal } = options;
  
  // Check if endpoint is already a full URL (e.g., master server URLs in remote auth mode)
  const isFullUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
  
  let url: string;
  if (isFullUrl) {
    // Use the full URL directly (e.g., for master server auth endpoints)
    url = endpoint;
  } else {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    // Build the full URL with base URL
    const baseUrl = getApiBaseUrl();
    url = `${baseUrl}${cleanEndpoint}`;
  }
  
  // Helper function to make the actual request
  const makeRequest = (authToken: string | null): Promise<Response> => {
    // Prepare headers
    const requestHeaders: HeadersInit = { ...headers };
    
    // Add authentication header if token is available
    if (authToken) {
      requestHeaders['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Add Content-Type for JSON requests (unless explicitly skipped for FormData)
    if (!skipJsonHeaders && body && typeof body === 'object' && !(body instanceof FormData)) {
      requestHeaders['Content-Type'] = 'application/json';
    }
    
    // Prepare request body
    let requestBody: string | FormData | undefined;
    if (body) {
      if (body instanceof FormData) {
        requestBody = body;
      } else if (typeof body === 'object') {
        requestBody = JSON.stringify(body);
      } else {
        requestBody = body;
      }
    }
    
    return fetch(url, {
      method,
      headers: requestHeaders,
      body: requestBody,
      signal,
    });
  };
  
  // First attempt with current token
  let token = getAuthToken();
  let response = await makeRequest(token);
  
  // If unauthorized and not a refresh endpoint, try to refresh token and retry
  if (response.status === 401 && !endpoint.includes('/auth/refresh') && isBrowser()) {
    // Check if authentication is disabled in development
    if (isAuthDisabled()) {
      console.log('Authentication disabled - skipping token refresh on 401');
      return response; // Return the 401 response as-is
    }

    console.log('API call unauthorized, attempting token refresh...');
    const refreshed = await refreshAccessToken();
    
    if (refreshed) {
      // Retry with new token
      token = getAuthToken();
      response = await makeRequest(token);
      console.log('Retried API call with refreshed token');
    } else {
      // Refresh failed - in desktop apps (Electron), don't redirect here.
      // Let the caller (AuthCheck, AuthContext) handle the failure with
      // Firebase re-auth or other persistent login mechanisms.
      console.log('Token refresh failed, clearing tokens');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      // Check if we're in a desktop app (Electron) - don't auto-redirect
      const hasElectronConfig = !!(window as any).__FIREBASE_CONFIG__ || !!(window as any).__REMOTE_AUTH_CONFIG__;
      const hasElectronUserAgent = navigator?.userAgent?.includes('Electron');
      const isElectron = hasElectronConfig || hasElectronUserAgent;
      
      // Only redirect if we're not in Electron and not already on an auth page
      if (!isElectron && !window.location.pathname.includes('/auth/') && !window.location.pathname.includes('/signin')) {
        window.location.href = '/auth/signin';
      }
    }
  }
  
  return response;
}

/**
 * Creates a query function for React Query that handles API responses consistently
 * @param endpoint The API endpoint to fetch data from (must start with /)
 * @returns A query function that fetches data and processes the response
 */
export function createQueryFn<TData>(endpoint: string): QueryFunction<TData, QueryKey> {
  return async () => {
    const response = await authenticatedFetch(endpoint);
    // handleApiResponse extracts user-friendly error messages from the API response
    return handleApiResponse<TData>(response);
  };
}

/**
 * Creates query options object for useQuery with standardized behavior
 * @param endpoint The API endpoint to fetch data from (must start with /)
 * @returns Query options object ready to use with useQuery
 */
export function createQueryOptions<TData>(endpoint: string) {
  return {
    queryKey: [endpoint],
    queryFn: createQueryFn<TData>(endpoint)
  };
}

/**
 * Creates a mutation function for React Query's useMutation that handles API responses consistently
 * @param endpoint The API endpoint to send the mutation to (must start with /)
 * @param method The HTTP method to use (POST, PUT, PATCH, DELETE)
 * @returns A mutation function that can be used with useMutation
 */
export function createMutationFn<TData, TVariables>(
  endpoint: string, 
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST'
) {
  return async (variables: TVariables): Promise<TData> => {
    const response = await authenticatedFetch(endpoint, {
      method,
      body: variables,
    });
    
    // handleApiResponse extracts user-friendly error messages from the API response
    return handleApiResponse<TData>(response);
  };
}

/**
 * Creates mutation options object for useMutation with standardized behavior
 * @param endpoint The API endpoint to send the mutation to (must start with /)
 * @param method The HTTP method to use (POST, PUT, PATCH, DELETE)
 * @returns Mutation options object ready to use with useMutation
 */
export function createMutationOptions<TData, TError = Error, TVariables = unknown, TContext = unknown>(
  endpoint: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST'
) {
  return {
    mutationFn: createMutationFn<TData, TVariables>(endpoint, method)
  };
}

/**
 * Makes an API request from server-side code and handles the response consistently
 * @param endpoint The API endpoint to send the request to (must start with /)
 * @param data Request payload
 * @param method The HTTP method to use (default: POST)
 * @param baseUrl Base URL for the API (default: http://localhost:5173)
 * @returns Promise with API response data
 */
export async function makeApiRequest<T>(
  endpoint: string, 
  data: any, 
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  baseUrl: string = 'http://localhost:5173'
): Promise<T> {
  // Only include body for non-GET methods when data is provided (not null/undefined)
  const shouldIncludeBody = method !== 'GET' && data != null;
  
  const response = await fetch(`${baseUrl}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    body: shouldIncludeBody ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API request failed (${response.status}): ${errorText}`);
  }

  return handleApiResponse<T>(response);
} 